import {
	AuthProvider,
	User as FirebaseUser,
	GithubAuthProvider,
	GoogleAuthProvider,
	OAuthProvider,
	SAMLAuthProvider,
	applyActionCode,
	checkActionCode,
	confirmPasswordReset,
	createUserWithEmailAndPassword,
	fetchSignInMethodsForEmail,
	getMultiFactorResolver,
	multiFactor,
	sendEmailVerification,
	sendPasswordResetEmail,
	signInAnonymously,
	signInWithEmailAndPassword,
	signInWithPopup,
} from "firebase/auth";
import { useState } from "react";

import { useTranslation } from "react-i18next";
import { firebaseAuth } from "../config/firebase.ts";
import { AUTH_METHODS, AUTH_SOURCES } from "../constants/AuthConstants";
import { POSTHOG_EVENTS } from "../constants/PostHogEvents";
import { USER_ORGANIZATION_ROLES } from "../constants/UserConstants.ts";
import { GITHUB_PROVIDER_ID, GOOGLE_PROVIDER_ID, MICROSOFT_PROVIDER_ID } from "../constants/common.ts";
import { addUserToOrgsIfMissing, createFirebaseUser, getUserWithUID } from "../helpers/firebase.ts";
import { IframePostMessageTypes } from "../models/new-iframe.ts";
import { UserOrganizationFlat } from "../models/organization.ts";
import iframeStore from "../pages/IframeNew/iframe-store.tsx";
import useErrorToast from "./useErrorToast.tsx";
import usePostHog from "./usePostHog.tsx";
import useSecureNavigate from "./useSecureNavigate.tsx";
import { useAppStore } from "./useStores.tsx";

export const useAuthService = () => {
	const errorToast = useErrorToast();
	const navigate = useSecureNavigate();
	const { t } = useTranslation();
	const { trackEvent } = usePostHog();
	const EXCLUSION_EXCEPTIONS_LIST = ["auth/popup-closed-by-user", "auth/cancelled-popup-request"];
	const [loading, setIsLoading] = useState<boolean>(false);
	const [errorMessage, setErrorMessage] = useState<boolean | string>(false);
	const { authStore, analyticsStore } = useAppStore();

	const loginAsGuest = async () => {
		return signInAnonymously(firebaseAuth);
	};

	const loginWithIframeSso = async (providerId: string) => {
		return await authenticateWithPopup(new SAMLAuthProvider(providerId), [
			{
				org_uid: iframeStore.iframe.organization.id,
				role: USER_ORGANIZATION_ROLES.USER,
				status: "active",
			},
		]);
	};

	const loginWithEmail = async (
		email: string,
		password: string,
		continueUrl?: string,
		state?: any,
		onSuccessCallback?: () => void,
		onMfaRequired?: (resolver: any) => void,
	) => {
		setIsLoading(true);
		try {
			await signInWithEmailAndPassword(firebaseAuth, email, password);

			onSuccessCallback?.();

			if (continueUrl) {
				const formattedUrl = continueUrl.startsWith("/") ? continueUrl : `/${continueUrl}`;
				navigate(formattedUrl, { state: state });
			}
			authStore.isLoginProcess = true;
		} catch (error: any) {
			if (error.code === "auth/multi-factor-auth-required") {
				// This is MFA verification required (user already has MFA set up)
				const resolver = getMultiFactorResolver(firebaseAuth, error);
				// Note: This should track MFA verification required, not setup started
				onMfaRequired?.(resolver);
			} else {
				// Track failed login
				trackEvent(POSTHOG_EVENTS.LOGIN_FAILED, {
					method: AUTH_METHODS.EMAIL,
					email: email,
					error: error.code || error.message,
				});
				setErrorMessage(error.message);
			}
		} finally {
			setIsLoading(false);
		}
	};

	async function createFirebaseUserAndUpdateStore(
		userToRegister: FirebaseUser,
		getInfo: boolean,
		username?: string,
		company?: string,
		role?: string,
		userOrganizations?: UserOrganizationFlat[],
		otherRoleDetails?: string,
	) {
		await createFirebaseUser(userToRegister, getInfo, username, company, role, userOrganizations, otherRoleDetails);

		// Retry getting user if it doesn't exist immediately
		let updatedUser = await getUserWithUID(userToRegister.uid);
		if (!updatedUser) {
			await new Promise((resolve) => setTimeout(resolve, 500));
			updatedUser = await getUserWithUID(userToRegister.uid);
		}

		await authStore.setUser(updatedUser);
		await authStore.setPostRegistrationConfigs();
		analyticsStore.logRegistrationEvent();

		// Track OAuth registration (this is called for new OAuth users)
		if (userToRegister.email) {
			const method = userToRegister.providerData[0]?.providerId?.includes(AUTH_METHODS.GOOGLE)
				? AUTH_METHODS.GOOGLE
				: userToRegister.providerData[0]?.providerId?.includes(AUTH_METHODS.GITHUB)
					? AUTH_METHODS.GITHUB
					: AUTH_METHODS.MICROSOFT;
			trackEvent(POSTHOG_EVENTS.USER_REGISTERED, {
				method: method,
				source: authStore.registrationSource || AUTH_SOURCES.EMAIL,
				email: userToRegister.email,
			});
		}

		return updatedUser;
	}

	const handleSignInResult = async (result: any, userOrganizations?: UserOrganizationFlat[]) => {
		const userToRegister = result.user;

		if (!userToRegister) {
			errorToast.showError();
			return Promise.reject(t("somethingWentWrong"));
		}

		let userObject = await getUserWithUID(userToRegister.uid);

		if (userObject) {
			// Login
			if (userOrganizations) {
				userObject = await addUserToOrgsIfMissing(userObject, userOrganizations);
			}
			authStore.isLoginProcess = true;
			return Promise.resolve(userObject?.getInfo ?? true);
		} else {
			// Signup
			try {
				const updatedUser = await createFirebaseUserAndUpdateStore(
					userToRegister,
					false,
					undefined,
					undefined,
					undefined,
					userOrganizations,
				);
				return Promise.resolve(updatedUser?.getInfo ?? true);
			} catch (error) {
				errorToast.showError(t("couldntRegisterFirebaseUser"));
				return Promise.reject(error);
			}
		}
	};

	const authenticateWithPopup = async (
		provider: AuthProvider,
		userOrganizations?: UserOrganizationFlat[],
		onMfaRequired?: (resolver: any, email: string) => void,
	) => {
		try {
			const result = await signInWithPopup(firebaseAuth, provider);

			return await handleSignInResult(result, userOrganizations);
		} catch (error: any) {
			if (iframeStore.isIframe()) {
				iframeStore.showIframeAuthErrorPage = true;
				iframeStore.sendPostMessage(IframePostMessageTypes.AuthFailed);
			}
			if (error.code === "auth/invalid-email") {
				errorToast.showError(t("emailIsInvalid"));
				if (!iframeStore.isIframe()) {
					return Promise.reject(t("emailIsInvalid"));
				}
			} else if (error.code === "auth/account-exists-with-different-credential") {
				const email = error.customData?.email;
				if (!email) {
					errorToast.showError(t("userExistsWithDifferentCredentials"));
					return Promise.reject(t("userExistsWithDifferentCredentials"));
				}
				const methods = await fetchSignInMethodsForEmail(firebaseAuth, email);

				const providerMap: Record<
					string,
					{
						provider: () => AuthProvider;
						errorKey: string;
						handleResult?: (result: any) => Promise<any>;
					}
				> = {
					[GOOGLE_PROVIDER_ID]: {
						provider: () => new GoogleAuthProvider(),
						errorKey: "signInWithGoogleInstead",
						handleResult: async (result) => await handleSignInResult(result, userOrganizations),
					},
					[GITHUB_PROVIDER_ID]: {
						provider: () => new GithubAuthProvider(),
						errorKey: "signInWithGithubInstead",
						handleResult: async (result) => {
							return await handleSignInResult(result, userOrganizations);
						},
					},
					[MICROSOFT_PROVIDER_ID]: {
						provider: () => new OAuthProvider(MICROSOFT_PROVIDER_ID),
						errorKey: "signInWithMicrosoftInstead",
						handleResult: async (result) => {
							return await handleSignInResult(result, userOrganizations);
						},
					},
				};

				const foundProviderId = Object.keys(providerMap).find((providerId) => methods.includes(providerId));

				if (foundProviderId) {
					const { provider, errorKey, handleResult } = providerMap[foundProviderId];
					errorToast.showError(t(errorKey));

					const result = await signInWithPopup(firebaseAuth, provider());
					if (handleResult) {
						return await handleResult(result);
					}
					return await handleSignInResult(result, userOrganizations);
				}

				if (methods.includes("password")) {
					errorToast.showError(t("signInWithEmailAndPasswordInstead"));
					return Promise.reject(t("signInWithEmailAndPasswordInstead"));
				}

				errorToast.showError(t("userExistsWithDifferentCredentials"));
				return Promise.reject(t("userExistsWithDifferentCredentials"));
			} else if (error.code === "auth/popup-blocked") {
				if (!iframeStore.isIframe()) {
					errorToast.showError(t("popupBlocked"));
					return Promise.reject(t("popupBlocked"));
				}
			} else if (error.code === "auth/multi-factor-auth-required") {
				const resolver = getMultiFactorResolver(firebaseAuth, error);
				onMfaRequired?.(resolver, error.customData._serverResponse.email);
				return Promise.reject(error);
			} else if (!EXCLUSION_EXCEPTIONS_LIST.includes(error.code)) {
				errorToast.showError();
				if (!iframeStore.isIframe()) {
					return Promise.reject(error);
				}
			}
			authStore.registrationSource = undefined;
		}
	};

	const registerWithEmail = async (
		username: string,
		email: string,
		password: string,
		company?: string,
		role?: string,
		otherRoleDetails?: string,
	) => {
		try {
			setIsLoading(true);
			const result = await createUserWithEmailAndPassword(firebaseAuth, email, password);
			const userToRegister = await result.user;
			if (!userToRegister) {
				errorToast.showError();
				return Promise.reject(t("somethingWentWrong"));
			}
			const updatedUser = await createFirebaseUserAndUpdateStore(
				userToRegister,
				true,
				username,
				company,
				role,
				undefined,
				otherRoleDetails,
			);
			setIsLoading(false);
			return Promise.resolve(updatedUser?.getInfo ?? true);
		} catch (error: any) {
			setIsLoading(false);
			setErrorMessage(error.message);
			authStore.registrationSource = undefined;
			return Promise.reject(error);
		}
	};

	const getActionCodeSettings = (continueUrl?: string, state?: any) => {
		let newUrl: URL;
		if (continueUrl) {
			newUrl = new URL(`${location.origin}${continueUrl}`);
		} else {
			newUrl = new URL(location.origin);
		}

		if (state?.prompt) {
			newUrl.searchParams.set("prompt", encodeURIComponent(state.prompt));
		}

		return {
			url: newUrl.toString(),
		};
	};

	const resetPassword = async (email: string, continueUrl?: string, state?: any) => {
		setIsLoading(true);
		return sendPasswordResetEmail(firebaseAuth, email, getActionCodeSettings(continueUrl, state))
			.then(() => {
				setErrorMessage(false);
			})
			.catch((error) => {
				setErrorMessage(error.message);
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const confirmPasswordResetFunction = async (code: string, newPassword: string) => {
		setIsLoading(true);
		try {
			const res = await confirmPasswordReset(firebaseAuth, code, newPassword);
			return res;
		} catch (error: any) {
			setErrorMessage(t(error.message));
			// var errorCode = error.code;
			// var errorMessage = error.message;
			return error;
		} finally {
			setIsLoading(false);
		}
	};

	const sendEmailVerificationFunction = async (continueUrl?: string, state?: any) => {
		return await sendEmailVerification(firebaseAuth.currentUser!, getActionCodeSettings(continueUrl, state));
	};

	const verifyUser = async (code: string) => {
		await checkActionCode(firebaseAuth, code);
		await applyActionCode(firebaseAuth, code);
	};

	const unenrollTotpMfa = async (): Promise<void> => {
		const currentUser: FirebaseUser | null = firebaseAuth.currentUser;
		if (!currentUser) throw Error("User not authentication");
		const totpFactor = multiFactor(currentUser).enrolledFactors.find((factor) => factor.factorId === "totp");

		if (!totpFactor) {
			throw new Error("TOTP is not enrolled for this user.");
		}

		await multiFactor(currentUser).unenroll(totpFactor.uid);
		authStore.isMfaSetupCompleted = false;
	};

	const revertMfaWithActionCode = async (actionCode: string): Promise<void> => {
		try {
			await checkActionCode(firebaseAuth, actionCode);
			await applyActionCode(firebaseAuth, actionCode);
			authStore.isMfaSetupCompleted = false;
		} catch (error: any) {
			throw new Error(error.message || "Failed to revert MFA");
		}
	};

	return {
		loginAsGuest,
		loginWithEmail,
		loginWithIframeSso,
		registerWithEmail,
		authenticateWithPopup,
		resetPassword,
		confirmPasswordResetFunction,
		sendEmailVerificationFunction,
		verifyUser,
		loading,
		errorMessage,
		setErrorMessage,
		unenrollTotpMfa,
		revertMfaWithActionCode,
	};
};
